/**
 * RebuildFromCacheCard (iter84.60.4.4)
 *
 * Non-destructive counterpart to HardWipeCard. Rebuilds fixtures from
 * the current upstream_cache snapshot AND preserves any existing
 * `match_overrides` on top. Then mirrors completed fixtures into the
 * legacy `matches` collection and recomputes team stats.
 *
 * Use when you want the iter84.60.4.3 KO-stage fix (or any future
 * fixture correction) to apply immediately, without waiting for the
 * ~5-min auto-sync loop, and WITHOUT losing hand-entered overrides.
 *
 * Compared to HardWipeCard:
 *   - Same rebuild + mirror + recompute pipeline
 *   - Overrides SURVIVE (preserve_overrides=True)
 *   - No preview / confirmation checkbox — the operation is safe
 *   - No audit row (Hard Wipe writes one because it destroys overrides)
 *
 * Endpoint: POST /api/site-admin/tournaments/{tid}/rebuild-from-cache
 */
import { useState } from "react";
import { RefreshCw, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function RebuildFromCacheCard({ tournaments = [] }) {
    // iter84.60.5.5 — ESPN + API-Football tournaments are eligible.
    const eligibleTournaments = tournaments.filter(
        (t) => (t.espn_sport && t.espn_league)
            || (t.api_football_league_id != null && t.api_football_season != null),
    );

    const [tournamentId, setTournamentId] = useState("");
    const [busy, setBusy] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const run = async () => {
        if (!tournamentId) {
            toast.error("Pick a tournament first.");
            return;
        }
        setBusy(true);
        setLastResult(null);
        try {
            const result = await api.siteRebuildFromCache(tournamentId);
            setLastResult(result);
            const stats = result.stats_result || {};
            toast.success(
                `Rebuild complete: ${result.fixtures_after} fixture(s), `
                + `${result.matches_mirrored ?? 0} match(es) mirrored, `
                + `standings recomputed for ${stats.teams_recomputed ?? "?"} team(s). `
                + `Overrides preserved: ${result.overrides_applied ?? 0}.`,
            );
        } catch (err) {
            const msg = err.response?.data?.detail || err.message;
            toast.error(`Rebuild failed: ${msg}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="brutal-card border-2 border-emerald-600 p-4 space-y-3" data-testid="rebuild-from-cache-card">
            <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-emerald-400" />
                <h3 className="text-sm font-bold uppercase tracking-widest text-emerald-400">
                    Rebuild from Cache (Non-destructive)
                </h3>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
                Applies the latest fixture pipeline to this tournament right now — no waiting for the ~5-min auto-sync. Rebuilds fixtures from the current cache snapshot, keeps every manual override intact, mirrors completed fixtures into the legacy matches collection with correct stages, and recomputes team + participant stats. Safe to run any time.
            </p>

            <div>
                <label className="block text-[10px] uppercase text-zinc-500 mb-1">Tournament (ESPN or API-Football)</label>
                <select
                    data-testid="rebuild-from-cache-tournament-select"
                    value={tournamentId}
                    onChange={(e) => { setTournamentId(e.target.value); setLastResult(null); }}
                    className="brutal-input w-full text-xs"
                >
                    <option value="">— pick a tournament —</option>
                    {eligibleTournaments.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
            </div>

            <button
                data-testid="rebuild-from-cache-execute-btn"
                onClick={run}
                disabled={busy || !tournamentId}
                className="brutal-btn w-full justify-center text-xs bg-emerald-500/20 border-emerald-500 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-40"
            >
                {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Rebuilding…</> : <><RefreshCw className="w-3 h-3" /> Rebuild Now</>}
            </button>

            {lastResult && (
                <div className="brutal-card-light p-3 space-y-1 border border-emerald-800" data-testid="rebuild-from-cache-result">
                    <p className="text-[11px] text-emerald-400 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Done — overrides preserved.
                    </p>
                    <ul className="text-[11px] text-zinc-300 space-y-0.5">
                        <li>Fixtures: <b className="text-zinc-100">{lastResult.fixtures_after}</b> ({lastResult.fixtures_before} before)</li>
                        <li>Matches mirrored: <b className="text-zinc-100">{lastResult.matches_mirrored ?? 0}</b> (wiped {lastResult.matches_wiped ?? 0} stale ESPN rows)</li>
                        <li>Overrides preserved: <b className="text-zinc-100">{lastResult.overrides_applied ?? 0}</b></li>
                    </ul>
                </div>
            )}
        </div>
    );
}
